import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getProject, createOverview, updateOverview } from "../../api/overview";
import TmapGeocoder from "../../components/viewer/tmap/TmapGeocoder";
import ImageModal from "../../components/common/ui/ImageModal";
import Carousel from "../../components/common/viewer/Carousel";

/* =========================
 * Constants & Utilities
 * ========================= */
const EMPTY_OVERVIEW = {
  project_name: "",
  client: "",
  CM_supervision: "",
  designer: "",
  constructor: "",
  types: "architecture",
  total_cost: "",
  contract_date: "",
  construction_date: "",
  estimated_completion_date: "",
  completion_date: "",
  site_location: "",
  business_scale: "",
};

const REQUIRED_FIELDS = [
  "project_name",
  "client",
  "CM_supervision",
  "constructor",
  "site_location",
];

const NUMBER_FIELDS = ["total_cost"];
const DECIMAL_PLACES = { total_cost: 1 };

// 표시용: 정수면 소수부 숨김, 소수면 말미 0 제거 + 천단위 콤마
const formatForDisplay = (raw) => {
  const s = String(raw ?? "")
    .replace(/,/g, "")
    .trim();
  if (!s) return "";
  if (!/^\d+(\.\d+)?$/.test(s)) return String(raw);

  const [intPart, decPart = ""] = s.split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const trimmedDec = decPart.replace(/0+$/, "");
  return trimmedDec ? `${formattedInt}.${trimmedDec}` : formattedInt;
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const safeValue = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number" && Number.isNaN(v)) return "";
  if (typeof v !== "string" && typeof v !== "number") return "";
  return String(v);
};

// 다운로드 파일명 생성
const getDownloadName = (item, idx) => {
  const rawNameFromItem = item.original_name || item.filename || "";
  let raw = "";
  try {
    const u = new URL(item.url, window.location.origin);
    raw = decodeURIComponent(u.pathname.split("/").pop() || "");
  } catch {}
  const candidate = rawNameFromItem || raw || `birdseye-${idx + 1}.jpg`;
  const dot = candidate.lastIndexOf(".");
  const base = dot > -1 ? candidate.slice(0, dot) : candidate;
  const ext = dot > -1 ? candidate.slice(dot) : "";
  const cleanedBase = base
    .replace(/([_\-\.])(?:[0-9a-f]{8,36})$/i, "")
    .replace(/([_\-\.])[A-Za-z0-9]{6,}$/i, "");
  const safe = (cleanedBase || base).replace(/[\/\\:*?"<>|]+/g, "_").trim();
  return (safe || `birdseye-${idx + 1}`) + (ext || ".jpg");
};

const forceDownload = async (url, filename) => {
  try {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank", "noopener");
  }
};

/* =========================
 * Reusable Inputs
 * ========================= */
// 공통 숫자 입력 (콤마, 소수 1자리 제한, onBlur에 display format)
const NumberInput = ({
  value,
  onChange,
  onBlur,
  readonly,
  className,
  style,
}) => (
  <input
    type="text"
    value={safeValue(value)}
    onChange={onChange}
    onBlur={onBlur}
    readOnly={readonly}
    inputMode="decimal"
    className={className}
    style={style}
  />
);

// 숫자 입력(단위 포함, 가로 폭 자동)
const NumberInputWithUnit = ({
  value,
  onChange,
  onBlur,
  readonly,
  numberAutoCls,
  getAutoSize,
  unit = "㎡",
  inputAlign = "left", // "left" | "center" | "right"
}) => {
  // 조회/수정 모두 값 길이에 맞춰 폭 자동 조절
  // - 수정 모드는 border/padding 때문에 조금 더 여유 폭을 준다
  const widthCh = getAutoSize
    ? getAutoSize(
        value,
        readonly ? 7 : 8, // 최소 폭: 조회 7ch, 수정 8ch
        readonly ? 2 : 4 // 여유 폭: 조회 2ch, 수정 4ch
      )
    : undefined;
  const style = widthCh ? { width: `${widthCh}ch` } : undefined;
  const alignCls =
    inputAlign === "center"
      ? "text-center"
      : inputAlign === "right"
      ? "text-right"
      : "";

  return (
    <>
      <NumberInput
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        readonly={readonly}
        className={`${numberAutoCls} ${alignCls}`}
        style={style}
      />
      <span className="shrink-0 pl-1">{unit}</span>
    </>
  );
};

/* =========================
 * Main Component
 * ========================= */
const OverviewRegister = ({
  is_superuser,
  initialForm,
  readonly = false,
  viewActions,
}) => {
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const location = useLocation();

  // Layout classes - 모바일 친화적
  const rowCls = readonly
    ? "flex flex-col mb-4 space-y-1"
    : "flex flex-col sm:flex-row sm:items-center mb-4 sm:mb-2 space-y-1 sm:space-y-0";
  const labelCls = readonly
    ? "w-full shrink-0 text-gray-600 text-xs font-medium mb-1.5"
    : "w-full sm:w-28 shrink-0 text-gray-600 text-xs font-medium mb-1.5 sm:mb-0 sm:pr-2 sm:py-2 sm:text-gray-900 sm:text-sm";
  const viewInputCls = readonly
    ? "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-900 text-sm focus:outline-none"
    : "w-full bg-transparent border-0 p-0 focus:outline-none focus:ring-0 text-gray-900 text-left text-sm sm:text-base";
  const editInputCls =
    "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";
  const editTextareaCls =
    "w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors min-h-[100px]";
  const inputBase = readonly ? viewInputCls : editInputCls;

  const viewNumberAutoCls =
    "bg-transparent border-0 p-0 focus:outline-none focus:ring-0 text-gray-900 w-auto min-w-[1ch] font-mono tabular-nums";
  const editNumberAutoCls =
    "bg-gray-50 border border-gray-200 rounded-lg px-2 py-2 w-auto min-w-[1ch] font-mono tabular-nums text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const numberAutoCls = readonly ? viewNumberAutoCls : editNumberAutoCls;

  const getAutoSize = (v, min = 1, pad = 0) =>
    Math.max(min, String(v ?? "").length + pad);

  const formFromState = location.state?.initialForm;
  const [form, setForm] = useState(
    formFromState ?? initialForm ?? { ...EMPTY_OVERVIEW }
  );
  const [touched, setTouched] = useState({});

  const [imgOpen, setImgOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [gallery, setGallery] = useState([]); // [{id?, url, file?}]
  const [deletedIds, setDeletedIds] = useState([]);
  const fileRef = useRef(null);

  // gallery 초기화
  useEffect(() => {
    const src = formFromState ?? initialForm ?? form ?? null;
    if (!src) return;
    const arr = Array.isArray(src.birdseye_images) ? src.birdseye_images : [];
    const mapped = arr.map((it) =>
      typeof it === "string" ? { url: it } : { id: it.id, url: it.url }
    );
    const single = src.birdseye_image ? [{ url: src.birdseye_image }] : [];
    setGallery(mapped.length ? mapped : single);
  }, [formFromState, initialForm]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const news = files.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setGallery((prev) => [...prev, ...news]);
    setForm((prev) => ({
      ...prev,
      birdseye_images: [
        ...(Array.isArray(prev.birdseye_images) ? prev.birdseye_images : []),
        ...files,
      ],
      birdseye_image: prev.birdseye_image || files[0] || "",
    }));
    e.target.value = "";
  };

  const removeAt = (i) => {
    if (!window.confirm("사진을 삭제하시겠습니까?")) return;
    setGallery((prev) => {
      const item = prev[i];
      if (item?.id) setDeletedIds((xs) => [...xs, item.id]);
      if (item?.file) {
        setForm((f) => ({
          ...f,
          birdseye_images: (Array.isArray(f.birdseye_images)
            ? f.birdseye_images
            : []
          ).filter((ff) => ff !== item.file),
        }));
      }
      return prev.filter((_, idx) => idx !== i);
    });
  };

  // 사업 규모 항목별 필드 관리
  const parseBusinessScale = (text) => {
    if (!text || !text.trim()) {
      return {
        usage: "",
        construction_area: "",
        building_coverage_ratio: "",
        total_floor_area: "",
        floor_area_ratio: "",
        structure: "",
        floors: "",
        max_height: "",
      };
    }

    const lines = text.split("\n").filter((line) => line.trim());
    const items = {};

    lines.forEach((line) => {
      const colonIndex = line.indexOf(":");
      if (colonIndex === -1) return;

      const label = line.substring(0, colonIndex).trim().replace(/\s+/g, "");
      const value = line.substring(colonIndex + 1).trim();

      // 라벨 매칭 (공백 제거 후 매칭)
      if (label.includes("용도")) {
        items.usage = value;
      } else if (label.includes("건축면적")) {
        items.construction_area = value;
      } else if (label.includes("건폐율")) {
        items.building_coverage_ratio = value;
      } else if (label.includes("연면적")) {
        items.total_floor_area = value;
      } else if (label.includes("용적률")) {
        items.floor_area_ratio = value;
      } else if (label.includes("구조")) {
        items.structure = value;
      } else if (label.includes("층수")) {
        items.floors = value;
      } else if (label.includes("최고높이") || label.includes("최고높이")) {
        items.max_height = value;
      }
    });

    return {
      usage: items.usage || "",
      construction_area: items.construction_area || "",
      building_coverage_ratio: items.building_coverage_ratio || "",
      total_floor_area: items.total_floor_area || "",
      floor_area_ratio: items.floor_area_ratio || "",
      structure: items.structure || "",
      floors: items.floors || "",
      max_height: items.max_height || "",
    };
  };

  const formatBusinessScale = (items) => {
    const lines = [];
    // 모든 항목을 항상 포함 (빈 값이어도)
    lines.push(`용      도 : ${items.usage || ""}`);
    lines.push(`건축면적 : ${items.construction_area || ""}`);
    lines.push(`건 폐 율 : ${items.building_coverage_ratio || ""}`);
    lines.push(`연 면 적 : ${items.total_floor_area || ""}`);
    lines.push(`용 적 률 : ${items.floor_area_ratio || ""}`);
    lines.push(`구      조 : ${items.structure || ""}`);
    lines.push(`층      수 : ${items.floors || ""}`);
    lines.push(`최고 높이 : ${items.max_height || ""}`);
    return lines.join("\n");
  };

  const [businessScaleItems, setBusinessScaleItems] = useState(() =>
    parseBusinessScale(form.business_scale)
  );

  useEffect(() => {
    setBusinessScaleItems(parseBusinessScale(form.business_scale));
  }, [form.business_scale]);

  const onBusinessScaleItemChange = (key) => (e) => {
    const newItems = { ...businessScaleItems, [key]: e.target.value };
    setBusinessScaleItems(newItems);
    const formatted = formatBusinessScale(newItems);
    setForm((prev) => ({ ...prev, business_scale: formatted }));
  };

  // initialForm 적용 + 숫자/날짜 표시 포맷
  useEffect(() => {
    const source = initialForm ?? formFromState;
    if (!source) return;
    setForm((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(source).map(([k, v]) => {
          // 숫자 필드(총 사업비 등)는 조회 화면과 동일한 포맷(3자리 콤마)으로 맞춰서 세팅
          if (NUMBER_FIELDS.includes(k)) {
            return [k, formatForDisplay(v)];
          }

          // 날짜 필드는 문자열로만 정리
          if (
            [
              "contract_date",
              "construction_date",
              "estimated_completion_date",
              "completion_date",
            ].includes(k)
          ) {
            return [k, toStr(v)];
          }

          // 나머지는 그냥 문자열 변환
          return [k, toStr(v)];
        })
      ),
    }));
  }, [initialForm, formFromState]);

  // 프로젝트 기본정보 로드 (등록 모드)
  useEffect(() => {
    if (formFromState || initialForm || !projectId) return;
    getProject(projectId)
      .then((data) => {
        const participants = data?.participants || {};
        const requiredRoles = ["발주처", "CM/감리단", "시공사"];
        const missingRoles = requiredRoles.filter(
          (role) => !participants[role]
        );
        if (missingRoles.length > 0) {
          alert(
            `아래 참여주체가 등록되어 있지 않습니다:\n${missingRoles.join(
              ", "
            )}\n\n설정을 눌러 '조직 관리 - 회사 등록' 페이지에서\n프로젝트 참여 매핑에서 역할을 매칭한 후,\n\n사업개요를 등록해주세요.`
          );
          navigate(`/project/${projectId}/system/user/pcp`);
          return;
        }

        // 설계사가 매핑되어 있지 않은 경우, 안내만 띄우고 페이지는 그대로 진입
        if (!participants["설계사"]) {
          alert(
            "설계사가 없습니다.\n\n추가하고 싶다면 프로젝트 참여주체(역할·매핑) 관리 페이지에서\n역할을 추가한 뒤 회사를 등록해주세요."
          );
        }

        setForm((prev) => ({
          ...prev,
          project_name: toStr(data?.name),
          site_location: toStr(data?.address),
          client: toStr(participants["발주처"]),
          CM_supervision: toStr(participants["CM/감리단"]),
          designer: participants["설계사"] ? toStr(participants["설계사"]) : "",
          constructor: toStr(participants["시공사"]),
        }));
      })
      .catch((err) => {
        console.error("프로젝트 불러오기 실패:", err);
        alert("프로젝트 정보를 불러오는 데 실패했습니다.");
      });
  }, [formFromState, initialForm, projectId, navigate]);

  const onChange = (key) => (e) => {
    if (readonly) return;
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [key]: safeValue(v) }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  // 숫자 입력 (공통)
  const onNumberChange = (key) => (e) => {
    if (readonly) return;
    let value = e.target.value.replace(/,/g, "");
    if (!/^(\d+)?(\.\d*)?$/.test(value) && value !== "") {
      alert("숫자를 입력해주세요.");
      return;
    }
    if (value.includes(".")) {
      const decPart = value.split(".")[1];
      // 소수부는 1자리까지만 허용
      if (decPart && decPart.length > 1) {
        alert("소수 1째자리까지만 입력할 수 있습니다.");
        return;
      }
    }
    if (value) {
      const [intPart, decPart] = value.split(".");
      const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      value =
        decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
    }
    setForm((prev) => ({ ...prev, [key]: toStr(value) }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  // 포커스 아웃 시 표시 포맷(정수면 소수부 숨김)
  const onNumberBlur = (key) => (e) => {
    if (readonly) return;
    setForm((prev) => ({ ...prev, [key]: formatForDisplay(e.target.value) }));
  };

  const isRequiredInvalid = (key) =>
    REQUIRED_FIELDS.includes(key) && touched[key] && !form[key];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readonly) return;

    const invalid = REQUIRED_FIELDS.filter((key) => !form[key]);
    if (invalid.length > 0) {
      setTouched((prev) => {
        const updated = { ...prev };
        invalid.forEach((key) => (updated[key] = true));
        return updated;
      });
      alert("필수 항목을 채워주세요. (해당 없는 항목은 '-' 입력 가능)");
      return;
    }

    try {
      const submitForm = { ...form, project: projectId };

      if (!submitForm.types) {
        submitForm.types = "architecture";
      }

      if (Array.isArray(form.birdseye_images) && form.birdseye_images.length) {
        submitForm.birdseye_images = form.birdseye_images;
        submitForm.birdseye_image =
          form.birdseye_images[0] ?? form.birdseye_image;
      }
      if (deletedIds.length) {
        submitForm.delete_birdseye_image_ids = deletedIds;
      }

      NUMBER_FIELDS.forEach((key) => {
        let val = safeValue(submitForm[key]).replace(/,/g, "");
        if (val !== "") {
          const places = DECIMAL_PLACES[key] ?? 0;
          submitForm[key] =
            places === 0
              ? Number.parseInt(val, 10)
              : Number(val).toFixed(places);
        }
      });

      [
        "contract_date",
        "construction_date",
        "estimated_completion_date",
        "completion_date",
      ].forEach((k) => {
        submitForm[k] = submitForm[k] ? String(submitForm[k]) : null;
      });

      if (form.id) {
        await updateOverview(projectId, form.id, submitForm);
        alert("사업개요가 수정되었습니다.");
      } else {
        await createOverview(projectId, submitForm);
        alert("사업개요가 저장되었습니다.");
      }
      navigate(`/project/${projectId}/overview`);
    } catch (err) {
      console.error("API 오류:", err?.response?.data ?? err);
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  const handleCancel = () => {
    if (
      window.confirm("변경사항이 저장되지 않습니다. 조회 페이지로 이동할까요?")
    ) {
      setForm(formFromState ?? initialForm ?? { ...EMPTY_OVERVIEW });
      setTouched({});
      navigate(`/project/${projectId}/overview`, { replace: true });
    }
  };

  /* =========================
   * Render
   * ========================= */
  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner mobile-bottom-spacing">
        <h2 className="text-lg font-bold mb-4">
          사업개요 {readonly ? "조회" : "등록/수정"}
        </h2>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-y-4">
          {/* 액션바 */}
          <div className="flex justify-end gap-2">
            {readonly ? (
              viewActions ?? null
            ) : (
              <>
                <button
                  type="submit"
                  className="px-3 py-2 rounded-xl border bg-white"
                >
                  저장
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-3 py-2 rounded-xl border bg-white"
                >
                  취소
                </button>
              </>
            )}
          </div>

          {/* ===== 왼쪽 패널 ===== */}
          <div className="flex flex-col">
            {/* 조감도 */}
            <fieldset
              className={`border ${
                readonly ? "border-gray-200 bg-white" : "border-slate-200"
              } rounded-xl p-4 sm:p-5 mb-5 shadow-sm`}
            >
              <legend
                className={`text-base sm:text-lg font-semibold ${
                  readonly ? "text-gray-800" : ""
                } px-2`}
              >
                조감도
              </legend>

              {readonly ? (
                gallery.length ? (
                  <Carousel
                    items={gallery}
                    heightClass="h-48 sm:h-64"
                    ariaLabel="조감도"
                    showArrows
                    renderItem={(item, i) => (
                      <div className="relative h-full w-full">
                        <button
                          type="button"
                          onClick={() => {
                            setImgSrc(item.url);
                            setImgOpen(true);
                          }}
                          onPointerDown={(e) => e.stopPropagation()}
                          data-carousel-interactive
                          className="h-full w-full"
                        >
                          <img
                            src={item.url}
                            alt=""
                            className="h-full w-full object-contain select-none"
                            draggable={false}
                          />
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            forceDownload(item.url, getDownloadName(item, i))
                          }
                          onPointerDown={(e) => e.stopPropagation()}
                          data-carousel-interactive
                          className="absolute right-2 bottom-2 px-2 py-1 text-xs rounded-md border bg-white shadow"
                          title="이미지 다운로드"
                        >
                          다운로드
                        </button>
                      </div>
                    )}
                  />
                ) : (
                  <div className="text-slate-500 text-center">
                    등록된 이미지가 없습니다.
                  </div>
                )
              ) : (
                <>
                  <Carousel
                    items={[...gallery, { __add: true }]}
                    heightClass="h-48 sm:h-64"
                    showArrows
                    renderItem={(item, i) =>
                      item.__add ? (
                        <button
                          type="button"
                          aria-label="이미지 추가"
                          title="이미지 추가"
                          onClick={() => fileRef.current?.click()}
                          onPointerDown={(e) => e.stopPropagation()}
                          data-carousel-interactive
                          className="relative h-full w-full bg-slate-800/60 text-white/85 select-none"
                        >
                          <span className="absolute inset-0 grid place-items-center pointer-events-none">
                            <span className="text-7xl leading-none translate-y-0/2">
                              ＋
                            </span>
                          </span>
                          <span className="absolute bottom-8 left-1/2 -translate-x-1/2 text-sm opacity-90">
                            이미지 추가
                          </span>
                        </button>
                      ) : (
                        <div className="relative h-full w-full bg-black/5">
                          <button
                            type="button"
                            onClick={() => {
                              setImgSrc(item.url);
                              setImgOpen(true);
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                            data-carousel-interactive
                            className="h-full w-full"
                          >
                            <img
                              src={item.url}
                              alt=""
                              className="h-full w-full object-contain select-none"
                              draggable={false}
                            />
                          </button>
                          <button
                            type="button"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={() => removeAt(i)}
                            className="absolute right-2 top-2 w-8 h-8 rounded-full bg-black/50 text-white"
                            aria-label="이미지 삭제"
                          >
                            ×
                          </button>
                        </div>
                      )
                    }
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onPickFiles}
                  />
                </>
              )}
            </fieldset>

            {/* 현장 위치(Map) */}
            <fieldset
              className={`border ${
                readonly ? "border-gray-200 bg-white" : "border-slate-200"
              } rounded-xl p-4 sm:p-5 mb-5 shadow-sm relative z-0`}
            >
              <legend
                className={`text-base sm:text-lg font-semibold ${
                  readonly ? "text-gray-800" : ""
                } px-2`}
              >
                현장 위치
              </legend>
              <div className="relative overflow-hidden isolate z-0 rounded-xl h-48 sm:h-64 mt-3">
                <TmapGeocoder
                  mapId="overview_site_map"
                  address={form.site_location || ""}
                />
              </div>
              <p className="text-xs text-gray-500 px-1 mt-3 mb-1">
                현장주소를 기준으로 위치가 표시됩니다.
              </p>
            </fieldset>
          </div>

          {/* ===== 오른쪽 패널 ===== */}
          <div>
            {/* 기본 정보 */}
            <fieldset
              className={`border ${
                readonly ? "border-gray-200 bg-white" : "border-slate-200"
              } rounded-xl p-4 sm:p-5 mb-5 shadow-sm`}
            >
              <legend
                className={`text-base sm:text-lg font-semibold ${
                  readonly ? "text-gray-800" : ""
                } px-2`}
              >
                기본 정보
              </legend>

              {/* 사업명 */}
              <div className="mb-4">
                <div className={rowCls}>
                  <span className={labelCls}>사업명</span>
                  <input
                    type="text"
                    value={form.project_name ?? ""}
                    onChange={onChange("project_name")}
                    className={`${
                      readonly ? viewInputCls : editInputCls
                    } flex-1`}
                    readOnly={readonly}
                  />
                </div>
              </div>

              {/* 참여자 */}
              <div
                className={`grid ${
                  readonly ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
                } gap-4 mb-4`}
              >
                {[
                  ["발주처", "client"],
                  ["CM/감리단", "CM_supervision"],
                  ["설계사", "designer"],
                  ["시공사", "constructor"],
                ].map(([label, key]) => (
                  <div className={rowCls} key={key}>
                    <span className={labelCls}>{label}</span>
                    <input
                      type="text"
                      value={form[key] ?? ""}
                      onChange={onChange(key)}
                      className={`${
                        readonly ? viewInputCls : editInputCls
                      } flex-1`}
                      readOnly={readonly}
                    />
                  </div>
                ))}
              </div>

              {/* 공사종류 */}
              <div className="mb-4">
                <div className={rowCls}>
                  <span className={labelCls}>공사종류</span>
                  <div className="flex-1 w-full">
                    {readonly ? (
                      <input
                        type="text"
                        value={form.types ?? ""}
                        readOnly
                        className={`${viewInputCls} flex-1`}
                      />
                    ) : (
                      <select
                        value={form.types ?? ""}
                        onChange={onChange("types")}
                        className={`${inputBase} flex-1`}
                      >
                        <option value="architecture">건축</option>
                        <option value="civil">토목</option>
                        <option value="mep">설비</option>
                        <option value="landscape">조경</option>
                        <option value="other">기타</option>
                      </select>
                    )}
                  </div>
                </div>
              </div>

              {/* 총사업비 */}
              <div className="mb-4">
                <div className={rowCls}>
                  <span className={labelCls}>총사업비</span>
                  <div className="flex items-center gap-2 flex-1">
                    {readonly ? (
                      <>
                        <span className={viewInputCls}>
                          {safeValue(form.total_cost) || "-"}
                        </span>
                        <span className="shrink-0 text-gray-600 text-sm">
                          원
                        </span>
                      </>
                    ) : (
                      <>
                        <NumberInputWithUnit
                          value={form.total_cost}
                          onChange={onNumberChange("total_cost")}
                          onBlur={onNumberBlur("total_cost")}
                          readonly={readonly}
                          numberAutoCls={numberAutoCls}
                          getAutoSize={getAutoSize}
                          unit="원"
                          inputAlign="left"
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* 날짜들 */}
              <div
                className={`grid ${
                  readonly ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"
                } gap-4`}
              >
                <div className={rowCls}>
                  <span className={labelCls}>계약일</span>
                  {readonly ? (
                    <input
                      type="text"
                      value={form.contract_date || ""}
                      readOnly
                      className={`${viewInputCls} flex-1`}
                    />
                  ) : (
                    <input
                      type="date"
                      value={form.contract_date || ""}
                      onChange={onChange("contract_date")}
                      className={`${inputBase} flex-1`}
                    />
                  )}
                </div>
                <div className={rowCls}>
                  <span className={labelCls}>착공일자</span>
                  {readonly ? (
                    <input
                      type="text"
                      value={form.construction_date || ""}
                      readOnly
                      className={`${viewInputCls} flex-1`}
                    />
                  ) : (
                    <input
                      type="date"
                      value={form.construction_date || ""}
                      onChange={onChange("construction_date")}
                      className={`${inputBase} flex-1`}
                    />
                  )}
                </div>
                <div className={rowCls}>
                  <span className={labelCls}>준공예정일</span>
                  {readonly ? (
                    <input
                      type="text"
                      value={form.estimated_completion_date || ""}
                      readOnly
                      className={`${viewInputCls} flex-1`}
                    />
                  ) : (
                    <input
                      type="date"
                      value={form.estimated_completion_date || ""}
                      onChange={onChange("estimated_completion_date")}
                      className={`${inputBase} flex-1`}
                    />
                  )}
                </div>
                <div className={rowCls}>
                  <span className={labelCls}>준공일자</span>
                  {readonly ? (
                    <input
                      type="text"
                      value={form.completion_date || ""}
                      readOnly
                      className={`${viewInputCls} flex-1`}
                    />
                  ) : (
                    <input
                      type="date"
                      value={form.completion_date || ""}
                      onChange={onChange("completion_date")}
                      className={`${inputBase} flex-1`}
                    />
                  )}
                </div>
              </div>
            </fieldset>

            {/* 대지 현황 */}
            <fieldset
              className={`border ${
                readonly ? "border-gray-200 bg-white" : "border-slate-200"
              } rounded-xl p-4 sm:p-5 mb-5 shadow-sm`}
            >
              <legend
                className={`text-base sm:text-lg font-semibold ${
                  readonly ? "text-gray-800" : ""
                } px-2`}
              >
                대지 현황
              </legend>

              <div className="mt-2">
                <div className={rowCls}>
                  <span className={labelCls}>현장주소</span>
                  <input
                    type="text"
                    value={form.site_location ?? ""}
                    onChange={onChange("site_location")}
                    className={`${
                      readonly ? viewInputCls : editInputCls
                    } flex-1`}
                    readOnly={readonly}
                  />
                </div>
              </div>
            </fieldset>

            {/* 사업 규모 */}
            <fieldset
              className={`border ${
                readonly ? "border-gray-200 bg-white" : "border-slate-200"
              } rounded-xl p-4 sm:p-5 mb-6 shadow-sm`}
            >
              <legend
                className={`text-base sm:text-lg font-semibold ${
                  readonly ? "text-gray-800" : ""
                } px-2`}
              >
                사업 규모
              </legend>
              {readonly ? (
                // readonly 모드: 각 항목을 개별 div로 분리해서 표시
                (() => {
                  const businessScale = safeValue(form.business_scale);
                  if (!businessScale || !businessScale.trim()) {
                    return (
                      <div className="text-gray-500 text-sm py-2">
                        등록된 내용이 없습니다.
                      </div>
                    );
                  }

                  // 줄바꿈으로 분리하고 각 줄을 파싱
                  const lines = businessScale
                    .split("\n")
                    .filter((line) => line.trim());
                  const items = lines.map((line, index) => {
                    // ":" 또는 " : "로 라벨과 값을 분리
                    const colonIndex = line.indexOf(":");
                    if (colonIndex === -1) {
                      // ":"가 없으면 전체를 값으로 표시
                      return { label: "", value: line.trim() };
                    }

                    const label = line.substring(0, colonIndex).trim();
                    const value = line.substring(colonIndex + 1).trim();

                    return { label, value };
                  });

                  return (
                    <div className="space-y-3 mt-2">
                      {items.map((item, index) => (
                        <div key={index} className="flex flex-col">
                          {item.label ? (
                            <>
                              <span className="text-xs text-gray-600 font-medium mb-1.5">
                                {item.label}
                              </span>
                              <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-900 text-sm">
                                {item.value || "-"}
                              </div>
                            </>
                          ) : (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5 text-gray-900 text-sm">
                              {item.value}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()
              ) : (
                // 편집 모드: 각 항목별로 개별 input 필드로 분리
                <div className="space-y-4 mt-2">
                  <div className={rowCls}>
                    <span className={labelCls}>용도</span>
                    <input
                      type="text"
                      value={businessScaleItems.usage}
                      onChange={onBusinessScaleItemChange("usage")}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 물류창고 (상온)"
                    />
                  </div>
                  <div className={rowCls}>
                    <span className={labelCls}>건축면적</span>
                    <input
                      type="text"
                      value={businessScaleItems.construction_area}
                      onChange={onBusinessScaleItemChange("construction_area")}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 36,450㎡ (11,026평)"
                    />
                  </div>
                  <div className={rowCls}>
                    <span className={labelCls}>건폐율</span>
                    <input
                      type="text"
                      value={businessScaleItems.building_coverage_ratio}
                      onChange={onBusinessScaleItemChange(
                        "building_coverage_ratio"
                      )}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 57.64%"
                    />
                  </div>
                  <div className={rowCls}>
                    <span className={labelCls}>연면적</span>
                    <input
                      type="text"
                      value={businessScaleItems.total_floor_area}
                      onChange={onBusinessScaleItemChange("total_floor_area")}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 127,179㎡ (38,472평)"
                    />
                  </div>
                  <div className={rowCls}>
                    <span className={labelCls}>용적률</span>
                    <input
                      type="text"
                      value={businessScaleItems.floor_area_ratio}
                      onChange={onBusinessScaleItemChange("floor_area_ratio")}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 139.02% (법정: 200%이하)"
                    />
                  </div>
                  <div className={rowCls}>
                    <span className={labelCls}>구조</span>
                    <input
                      type="text"
                      value={businessScaleItems.structure}
                      onChange={onBusinessScaleItemChange("structure")}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 철근콘크리트조 + PC구조 + PEB"
                    />
                  </div>
                  <div className={rowCls}>
                    <span className={labelCls}>층수</span>
                    <input
                      type="text"
                      value={businessScaleItems.floors}
                      onChange={onBusinessScaleItemChange("floors")}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 지하 2층 ~ 지상 4층"
                    />
                  </div>
                  <div className={rowCls}>
                    <span className={labelCls}>최고 높이</span>
                    <input
                      type="text"
                      value={businessScaleItems.max_height}
                      onChange={onBusinessScaleItemChange("max_height")}
                      className={`${editInputCls} flex-1`}
                      placeholder="예: 33.4m (법정: 50m 이하)"
                    />
                  </div>
                </div>
              )}
              {!readonly && isRequiredInvalid("business_scale") && (
                <div className="text-xs text-red-500 mt-1">
                  * 필수 항목입니다.
                </div>
              )}
            </fieldset>
          </div>
        </form>

        <ImageModal
          open={imgOpen}
          src={imgSrc}
          alt="조감도"
          onClose={() => setImgOpen(false)}
        />
      </div>
    </div>
  );
};

export default OverviewRegister;
